/**
 * TRADE 定制：解除 viewport 缩放封锁，让移动端放大到 200% 仍可操作
 *
 * 【为什么需要这个】
 * ChatWILL（Willlink）客服 app 会在运行时改写 viewport meta。它的脚本是
 *   https://client.chatwill.ai/willlink-entrance.min.js
 * 里面的逻辑相当于：
 *   e.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
 * 而且找不到 meta 时它还会自己 createElement 造一个塞进 <head>。
 *
 * 于是出现一个很迷惑的现象：主题源码里那个标签是干净的 ——
 *   <meta name="viewport" content="width=device-width,initial-scale=1">
 * 但 JS 跑完之后，浏览器里变成了被锁死的那一版，移动端没法双指放大。
 * （查证方法：控制台跑 document.querySelector('meta[name=viewport]').content）
 * 无障碍审核里「放大至 200% 仍可操作」这一项就是在这里挂掉的。
 *
 * 【为什么要在主题里反制】
 * 改写发生在 app 的 CDN 脚本里，不在主题内，主题代码拦不住源头。
 * 所以这里盯着 <head>，一旦 content 里出现 maximum-scale / user-scalable
 * 就立刻把那两个指令摘掉，其余指令原样保留。
 *
 * 【为什么是「摘指令」而不是「写死整个 content」】
 *   - 只移除封锁项，不影响其它 viewport 指令（例如以后要加 viewport-fit=cover）
 *   - 天然幂等：已经干净的字符串再摘一次还是同一个值 → 不写回 → 不触发变更
 *     → 不会和 MutationObserver 来回打架（打乒乓）。收敛靠的就是这个幂等性，
 *     所以下面故意没有再加重入锁。
 *
 * 【生效范围】
 * 动态改 viewport 的效果因浏览器而异：Android Chrome 会即时生效（也就是真正
 * 被锁住的那批用户，正好是能修好的那批）；iOS Safari 从 iOS 10 起本来就无视
 * user-scalable=no，不受影响。
 *
 * 【副作用提醒】
 * app 锁缩放本意是保护它自己的客服窗界面。摘掉之后，用户放大时客服悬浮窗
 * 可能出现轻微错位，但页面本身不受影响。
 */
(() => {
  const META_SELECTOR = 'meta[name="viewport"]';
  const BLOCKED = ['maximum-scale', 'user-scalable']; // 只针对这两项，其余保留
  const POLL_MS = 500;
  const POLL_TIMES = 60; // 500ms x 60 = 30s 巡逻窗口，覆盖 app 的延迟改写

  /**
   * 摘掉封锁缩放的指令，其余原样返回。
   * 幂等：入参已经干净时返回同一个字符串。
   * @param {string} content viewport 的 content 值
   * @returns {string} 清理后的 content
   */
  function sanitize(content) {
    if (!content) return content;
    return content
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => !BLOCKED.some((blocked) => token.toLowerCase().startsWith(blocked)))
      .join(', ');
  }

  /** 检查并修复；已经干净就什么都不做（这一步保证不会死循环） */
  function enforce() {
    const meta = document.querySelector(META_SELECTOR);
    if (!meta) return;

    const current = meta.getAttribute('content') || '';
    const fixed = sanitize(current);
    if (fixed === current) return;

    meta.setAttribute('content', fixed);
  }

  // 1) 立刻先跑一次
  enforce();

  // 2) 持续盯着 <head>：content 被改、meta 被删掉重建，都会立刻改回来
  function watch() {
    if (!document.head) return;
    new MutationObserver(enforce).observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['content'],
    });
  }
  if (document.head) {
    watch();
  } else {
    document.addEventListener('DOMContentLoaded', watch, { once: true });
  }

  // 3) 兜底巡逻：app 也可能在很晚的时机才改写（例如客服窗被打开时）。
  //    observer 虽然也能抓到，但多一层保险；跑满 30s 就自动停，不留常驻定时器。
  let ticks = 0;
  const timer = setInterval(() => {
    enforce();
    if (++ticks >= POLL_TIMES) clearInterval(timer);
  }, POLL_MS);
})();
